import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Button, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import DeleteIcon from "@mui/icons-material/Delete";
import BasicDatePicker from "common/Pickers/Pickers";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

interface InventoryItemsTableProps {
  form?: any;
  columns: any[];
  handleInputChange: (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => void;
  rows: any[];
  typeName: string;
  items?: any[];
  currency?: string;
  DeleteItem: (row: any, typeName: string, index?: number) => void;
  params?: any;
  handleItem?: (row: any, typeName: string, action: string) => void;
  checkStatus?: number | null;
  viewDelete?: boolean;
  adjustmentTypesList?: any;
  hideDelete?: boolean;
  enableQuantity?: boolean;
  enableDelete?: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  dropdownPage: number;
  searchLoading: string | null;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  itemsResponse: any;
  binOptions?: { value: number; label: string }[];
  onItemSelected?: (index: number, selectedOption: any) => void;
}

export const InventoryItemsTable: React.FC<InventoryItemsTableProps> = ({
  form,
  columns,
  handleInputChange,
  rows,
  typeName,
  items,
  currency,
  DeleteItem,
  params,
  handleItem,
  checkStatus,
  viewDelete,
  adjustmentTypesList,
  hideDelete,
  enableQuantity,
  enableDelete,
  handleDropdownActions,
  itemsResponse,
  dropdownPage,
  searchLoading,
  dropdownSearchTerm,
  setDropdownSearchTerm,
  setDropdownPage,
  binOptions = [],
  onItemSelected,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const previousValueRef = React.useRef<string | number | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      (rows || [])?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows],
  );

  const visibleColumns = React.useMemo(
    () =>
      (columns || []).filter(
        (column) =>
          column.id !== "itemId" &&
          column.id !== "itemCode" &&
          column.id !== "unitId",
      ),
    [columns],
  );

  const getColumnWidthSx = (column: any) => {
    if (column.id === "itemName") return { width: "15%", minWidth: 140 };
    if (column.id === "supplierSKU") return { width: "7%", minWidth: 72 };
    if (column.id === "baseItemQuantity") return { width: "5%", minWidth: 72 };
    if (column.id === "locationCode") return { width: "10%", minWidth: 72 };
    if (column.field === "quantity" || column.id === "quantity")
      return {
        // Wider only when +/- stepper buttons are actually rendered.
        width: column.field === "quantity" && enableQuantity ? "9%" : "5%",
        minWidth: column.field === "quantity" && enableQuantity ? 100 : 52,
      };
    if (column.id === "unitTitle") return { width: "5%", minWidth: 48 };
    if (column.id === "unitPrice") return { width: "7%", minWidth: 64 };
    if (column.id === "discount") return { width: "6%", minWidth: 56 };
    if (column.id === "batchNumber") return { width: "7%", minWidth: 64 };
    if (column.id === "destinationBinId")
      return { width: "12%", minWidth: 110 };
    if (column.id === "receivedDate" || column.id === "expiryDate")
      return { width: "9%", minWidth: 100 };
    return { width: "8%", minWidth: 70 };
  };

  const quantityBtnSx = {
    minWidth: "28px !important",
    width: 32,
    height: 32,
    padding: 0,
    flexShrink: 0,
    margin: "0 4px",
    "&:hover": {
      backgroundColor: "rgba(76, 0, 85, 0.08)",
    },
    "&:active": {
      backgroundColor: "rgba(76, 0, 85, 0.16)",
    },
    "&:focus": {
      backgroundColor: "rgba(76, 0, 85, 0.24)",
    },
  };

  return (
    <Box sx={{ width: "100%" }} id="inventorytable">
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table
          sx={{ width: "100%", minWidth: 900, tableLayout: "fixed" }}
          aria-labelledby="tableTitle"
          size={"small"}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? "right" : "left"}
                  padding={column.disablePadding ? "none" : "normal"}
                  sortDirection={false}
                  sx={{
                    ...getColumnWidthSx(column),
                    wordBreak: "break-word",
                    verticalAlign: "middle",
                  }}
                >
                  <span className="font-bold">{column.label}</span>
                </TableCell>
              ))}
              {!hideDelete ? (
                <TableCell className="hide-on-print" sx={{ width: "6%" }} />
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;
              const selectedIds =
                (params?.id && typeName === "purchaseInvoiceItem") ||
                typeName === "purchaseOrderItems" ||
                typeName === "stockTransferItem" ||
                typeName === "stockReceiptItems"
                  ? form.watch(typeName)?.map((row: any) => row.itemId) || []
                  : [];
              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  sx={{ cursor: "pointer" }}
                >
                  {columns
                    .filter(
                      (column) =>
                        column.id !== "itemId" &&
                        column.id !== "itemCode" &&
                        column.id !== "unitId",
                    )
                    .map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.numeric ? "right" : "left"}
                        padding={column.disablePadding ? "none" : "normal"}
                        component={column.id === "itemName" ? "th" : undefined}
                        id={column.id === "itemName" ? labelId : undefined}
                        scope={column.id === "itemName" ? "row" : undefined}
                        sx={getColumnWidthSx(column)}
                      >
                        {column.id === "itemName" ? (
                          <Controller
                            name={`${typeName}.${index}.${column.id}`}
                            control={form.control}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  "& .dropdown": { height: "0 !important" },
                                }}
                              >
                                <DynamicSelect
                                  label=""
                                  {...field}
                                  value={
                                    form.watch(`${typeName}.${index}.itemId`)
                                      ? {
                                          value: form.watch(
                                            `${typeName}.${index}.itemId`,
                                          ),
                                          label:
                                            form.watch(
                                              `${typeName}.${index}.itemName`,
                                            ) ||
                                            "" +
                                              " " +
                                              form.watch(
                                                `${typeName}.${index}.itemCode`,
                                              ),
                                          itemCode:
                                            form.watch(
                                              `${typeName}.${index}.itemCode`,
                                            ) || "",
                                          unitId: form.watch(
                                            `${typeName}.${index}.unitId`,
                                          ),
                                          unitTitle: form.watch(
                                            `${typeName}.${index}.unitTitle`,
                                          ),
                                          baseItemQuantity: form.watch(
                                            `${typeName}.${index}.baseItemQuantity`,
                                          ),
                                        }
                                      : null
                                  }
                                  options={items?.map(
                                    (item: {
                                      id: number;
                                      title: string;
                                      itemCode: string;
                                      unitId: number | null;
                                      stockQuantity: number;
                                      itemCost: number;
                                      unitTitle: string;
                                      baseItemQuantity: number;
                                      locationCode?: string;
                                    }) => ({
                                      value: item.id,
                                      label: item.title + ` ` + item.itemCode,
                                      itemCode: item.itemCode,
                                      unitId: item.unitId,
                                      stockQuantity: item?.stockQuantity || 1,
                                      itemCost: item?.itemCost || 0,
                                      unitTitle: item?.unitTitle || "",
                                      baseItemQuantity:
                                        item?.baseItemQuantity || 0,
                                      locationCode: item?.locationCode || "",
                                      isDisabled:
                                        ((params?.id &&
                                          typeName === "purchaseInvoiceItem") ||
                                          typeName === "purchaseOrderItems" ||
                                          typeName === "stockTransferItem" ||
                                          typeName === "stockReceiptItems") &&
                                        selectedIds.includes(item.id),
                                    }),
                                  )}
                                  isSearchable
                                  onChange={(selectedOption: any) => {
                                    field.onChange(selectedOption, column.type);
                                    handleInputChange(
                                      index,
                                      "itemId",
                                      selectedOption.value,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "itemName",
                                      selectedOption.label,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "itemCode",
                                      selectedOption.itemCode,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "unitId",
                                      selectedOption.unitId,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "unitTitle",
                                      selectedOption.unitTitle,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "baseItemQuantity",
                                      selectedOption.baseItemQuantity,
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "locationCode",
                                      selectedOption.locationCode || "",
                                      typeName,
                                      column.type,
                                    );
                                    handleInputChange(
                                      index,
                                      "destinationBinId",
                                      null,
                                      typeName,
                                      "select",
                                    );
                                    handleInputChange(
                                      index,
                                      "quantity",
                                      1,
                                      typeName,
                                      column.type,
                                    );
                                    if (
                                      columns.some(
                                        (column) =>
                                          column.id === "quantityAdjusted",
                                      )
                                    )
                                      handleInputChange(
                                        index,
                                        "quantityAdjusted",
                                        1,
                                        typeName,
                                        column.type,
                                      );
                                    handleInputChange(
                                      index,
                                      "unitPrice",
                                      selectedOption.itemCost,
                                      typeName,
                                      column.type,
                                    );
                                    if (
                                      columns.some(
                                        (column) =>
                                          column.id === "unitCostAdjusted",
                                      )
                                    )
                                      handleInputChange(
                                        index,
                                        "unitCostAdjusted",
                                        selectedOption.itemCost,
                                        typeName,
                                        column.type,
                                      );
                                    form.setValue(
                                      `${typeName}.${index}.${"itemId"}`,
                                      selectedOption.value,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"itemName"}`,
                                      selectedOption.label,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"itemCode"}`,
                                      selectedOption.itemCode,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"unitId"}`,
                                      selectedOption.unitId,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"unitTitle"}`,
                                      selectedOption.unitTitle,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"baseItemQuantity"}`,
                                      selectedOption.baseItemQuantity,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"locationCode"}`,
                                      selectedOption.locationCode || "",
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.destinationBinId`,
                                      null,
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${"quantity"}`,
                                      1,
                                    );
                                    if (
                                      columns.some(
                                        (column) =>
                                          column.id === "quantityAdjusted",
                                      )
                                    )
                                      form.setValue(
                                        `${typeName}.${index}.${"quantityAdjusted"}`,
                                        1,
                                      );
                                    form.setValue(
                                      `${typeName}.${index}.${"unitPrice"}`,
                                      selectedOption.itemCost,
                                    );
                                    if (
                                      columns.some(
                                        (column) =>
                                          column.id === "unitCostAdjusted",
                                      )
                                    )
                                      form.setValue(
                                        `${typeName}.${index}.${"unitCostAdjusted"}`,
                                        selectedOption.itemCost,
                                      );
                                    if (params?.id && !row.id)
                                      handleItem?.(row, typeName, "create");
                                    else if (params?.id && row.id)
                                      handleItem?.(row, typeName, "update");
                                    onItemSelected?.(index, selectedOption);
                                  }}
                                  isDisabled={
                                    checkStatus === 2 ||
                                    checkStatus === 3 ||
                                    checkStatus === 4
                                  }
                                  isLoading={
                                    searchLoading ===
                                    `${typeName}.${index}.${column.id}`
                                  }
                                  onInputChange={(
                                    search: string,
                                    { action }: { action: string },
                                  ) => {
                                    if (
                                      action === "input-change" ||
                                      (action !== "set-value" &&
                                        action !== "menu-close" &&
                                        action !== "input-blur")
                                    )
                                      handleDropdownActions(
                                        `${typeName}.${index}.${column.id}`,
                                        "searchIt",
                                        search,
                                      );
                                    else if (
                                      action === "input-blur" ||
                                      action === "menu-close"
                                    ) {
                                      if (
                                        dropdownSearchTerm ||
                                        dropdownPage > 1
                                      )
                                        handleDropdownActions(
                                          `${typeName}.${index}.${column.id}`,
                                          "searchIt",
                                          "",
                                        );
                                      else setDropdownSearchTerm("");
                                      setDropdownPage(1);
                                    }
                                  }}
                                  onMenuScrollToBottom={() => {
                                    if (
                                      !(itemsResponse?.pages[0]?.data as any)
                                        .last
                                    )
                                      handleDropdownActions(
                                        `${typeName}.${index}.${column.id}`,
                                        "loadNext",
                                      );
                                  }}
                                  page={dropdownPage || 1}
                                />
                              </Box>
                            )}
                          />
                        ) : column.id === "baseItemQuantity" ? (
                          <span className="font-bold">
                            {form.watch(
                              `${typeName}.${index}.${"baseItemQuantity"}`,
                            ) || 0}
                          </span>
                        ) : column.id === "locationCode" ? (
                          <span className="font-bold">
                            {form.watch(`${typeName}.${index}.locationCode`) ||
                              row?.locationCode ||
                              "-"}
                          </span>
                        ) : column.id === "destinationBinId" ? (
                          <Controller
                            name={`${typeName}.${index}.destinationBinId`}
                            control={form.control}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  "& .dropdown": { height: "0 !important" },
                                }}
                              >
                                <DynamicSelect
                                  label=""
                                  {...field}
                                  value={
                                    form.watch(
                                      `${typeName}.${index}.destinationBinId`,
                                    ) || null
                                  }
                                  options={binOptions}
                                  isSearchable
                                  isClearable
                                  defaultPlaceholder="phSelectBin"
                                  onChange={(selectedOption: any) => {
                                    field.onChange(selectedOption);
                                    handleInputChange(
                                      index,
                                      "destinationBinId",
                                      selectedOption,
                                      typeName,
                                      "select",
                                    );
                                  }}
                                  isDisabled={
                                    checkStatus === 2 ||
                                    checkStatus === 3 ||
                                    checkStatus === 4
                                  }
                                  isLoading={searchLoading === "bins"}
                                />
                              </Box>
                            )}
                          />
                        ) : column.id === "discount" ? ( // Added validation for discount
                          <Controller
                            name={`${typeName}.${index}.${column.id}`}
                            control={form.control}
                            render={({ field }) => (
                              <Box sx={{ "& .Mui-disabled": { opacity: 0.6 } }}>
                                <TextField
                                  {...field}
                                  type="number"
                                  placeholder={t("discount")}
                                  title={t("discount")}
                                  fullWidth
                                  onChange={(e) => {
                                    let value = parseInt(e.target.value);
                                    if (value < 0) value = 0; // Ensure discount is not less than 0
                                    if (value > 100) value = 100; // Ensure discount is not greater than 100
                                    handleInputChange(
                                      index,
                                      column.id,
                                      value,
                                      typeName,
                                      column.type,
                                    );
                                    field.onChange(value);
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === "") {
                                      field.onChange("0");
                                      handleInputChange(
                                        index,
                                        column.id,
                                        parseInt(e.target.value) || 0,
                                        typeName,
                                        column.type,
                                      );
                                    }
                                  }}
                                  defaultValue={0}
                                  disabled={
                                    checkStatus === 2 ||
                                    checkStatus === 3 ||
                                    checkStatus === 4
                                  }
                                />
                              </Box>
                            )}
                          />
                        ) : column.id === "unitTitle" ? (
                          <span className="font-bold whitespace-nowrap">
                            {form.watch(
                              `${typeName}.${index}.${"unitTitle"}`,
                            ) ||
                              row?.unitTitle ||
                              t("unit(s)")}
                          </span>
                        ) : column.id === "total" ? (
                          <span className="font-bold">
                            {new Intl.NumberFormat(
                              "en-US",
                              currency
                                ? {
                                    style: "currency",
                                    currency: currency || "",
                                  }
                                : {},
                            ).format(
                              parseInt(
                                form.watch(
                                  `${typeName}.${index}.${"quantity"}`,
                                ),
                              ) *
                                (parseFloat(
                                  form.watch(
                                    `${typeName}.${index}.${"unitPrice"}`,
                                  ),
                                ) -
                                  (parseFloat(
                                    form.watch(
                                      `${typeName}.${index}.${"unitPrice"}`,
                                    ),
                                  ) *
                                    parseFloat(
                                      form.watch(
                                        `${typeName}.${index}.${"discount"}`,
                                      ) || 0,
                                    )) /
                                    100) || 0,
                            )}
                          </span>
                        ) : column.type === "date" ? (
                          <Controller
                            name={`${typeName}.${index}.${column.id}`}
                            control={form.control}
                            rules={{
                              validate: (value) => {
                                const date = new Date(value);
                                return isNaN(date.getTime())
                                  ? t("Invalid Date")
                                  : true;
                              },
                            }}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  "& .dateerror": { height: "0 !important" },
                                  "& .Mui-disabled": { opacity: 0.6 },
                                }}
                              >
                                <BasicDatePicker
                                  type="datePicker"
                                  {...field}
                                  fullWidth
                                  onChange={(value: any) => {
                                    field.onChange(
                                      new Date(value)
                                        .toLocaleDateString("en-CA", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                        })
                                        ?.split("/")
                                        .reverse()
                                        .join("-"),
                                    );
                                    form.setValue(
                                      `${typeName}.${index}.${column.id}`,
                                      new Date(value)
                                        .toLocaleDateString("en-CA", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                        })
                                        ?.split("/")
                                        .reverse()
                                        .join("-"),
                                    );
                                    handleInputChange(
                                      index,
                                      column.id,
                                      new Date(value)
                                        .toLocaleDateString("en-CA", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                        })
                                        ?.split("/")
                                        .reverse()
                                        .join("-"),
                                      typeName,
                                      column.type,
                                    );
                                    if (params?.id && !row.id)
                                      handleItem?.(row, typeName, "create");
                                    else if (params?.id && row.id)
                                      handleItem?.(row, typeName, "update");
                                  }}
                                  disable={
                                    params?.id
                                      ? checkStatus === 2 ||
                                        checkStatus === 3 ||
                                        checkStatus === 4
                                      : false
                                  }
                                  maxDate={
                                    column.id === "receivedDate"
                                      ? dayjs()
                                      : null
                                  }
                                  value={
                                    column.id === "receivedDate"
                                      ? form.watch(
                                          `${typeName}.${index}.${column.id}`,
                                        ) ||
                                        dayjs(new Date())?.format("YYYY-MM-DD")
                                      : form.watch(
                                          `${typeName}.${index}.${column.id}`,
                                        )
                                  }
                                />
                              </Box>
                            )}
                          />
                        ) : column.id === "adjustmentType" ? (
                          <Controller
                            name={`${typeName}.${index}.${column.id}`}
                            control={form.control}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  "& .dropdown": { height: "0 !important" },
                                }}
                              >
                                <DynamicSelect
                                  label=""
                                  {...field}
                                  value={
                                    form.watch(
                                      `${typeName}.${index}.adjustmentType`,
                                    )
                                      ? form.watch(
                                          `${typeName}.${index}.adjustmentType`,
                                        )
                                      : { value: 1, label: t("remove") }
                                  }
                                  options={adjustmentTypesList}
                                  onChange={(selectedOption: any) => {
                                    field.onChange(selectedOption, column.type);
                                    handleInputChange(
                                      index,
                                      "adjustmentType",
                                      selectedOption.value,
                                      typeName,
                                      column.type,
                                    );
                                    if (params?.id && !row.id)
                                      handleItem?.(row, typeName, "create");
                                    else if (params?.id && row.id)
                                      handleItem?.(row, typeName, "update");
                                  }}
                                  isDisabled={
                                    checkStatus === 2 ||
                                    checkStatus === 3 ||
                                    checkStatus === 4
                                  }
                                />
                              </Box>
                            )}
                          />
                        ) : column.id === "adjustmentReason" ? (
                          <>
                            <TextField
                              {...form.register(
                                `${typeName}.${index}.${column.id}`,
                                {
                                  validate: {
                                    required: (value: string) =>
                                      value?.trim() !== "" ||
                                      t("This field is required."),
                                  },
                                },
                              )}
                              placeholder={t("adjustmentReason") + " *"}
                              title={t("adjustmentReason") + " *"}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              multiline
                              rows={2}
                              sx={{
                                "& .MuiInputBase-multiline": {
                                  border: !form
                                    .watch(`${typeName}.${index}.${column.id}`)
                                    ?.trim()
                                    ? "1px solid red !important"
                                    : "1px solid #C4C4C4 !important",
                                  padding: "5px 10px",
                                },
                                "& .MuiInputBase-fullWidth.MuiInputBase-multiline":
                                  { width: "100%" },
                              }}
                              onChange={(e) => {
                                handleInputChange(
                                  index,
                                  column.id,
                                  e.target.value,
                                  typeName,
                                  column.type,
                                );
                                form.trigger([
                                  `${typeName}.${index}.${column.id}`,
                                ]);
                              }}
                              disabled={
                                checkStatus === 2 ||
                                checkStatus === 3 ||
                                checkStatus === 4
                              }
                            />
                          </>
                        ) : (
                          <Controller
                            name={`${typeName}.${index}.${column.id}`}
                            control={form.control}
                            render={({ field }) => (
                              <Box
                                className="flex"
                                sx={{
                                  alignItems: "center",
                                  width: "100%",
                                  minWidth: 0,
                                  "& .Mui-disabled": { opacity: 0.6 },
                                  "& .MuiFormControl-root": { minWidth: 0 },
                                  "& .MuiInputBase-root": {
                                    minWidth: 0,
                                    width: "100%",
                                  },
                                }}
                              >
                                {column.field === "quantity" && !params?.id ? (
                                  <Button
                                    aria-label="reduce"
                                    sx={{
                                      ...quantityBtnSx,
                                      borderRadius: "6px 0 0 6px",
                                      background: "#F2E5E9 !important",
                                      borderColor: "#FC5C57",
                                    }}
                                    onClick={() => {
                                      form.setValue(
                                        `${typeName}.${index}.${column.id}`,
                                        Math.max(
                                          1,
                                          form.watch(
                                            `${typeName}.${index}.${column.id}`,
                                          ) - 1,
                                        ),
                                      );
                                      handleInputChange(
                                        index,
                                        column.id,
                                        form.watch(
                                          `${typeName}.${index}.${column.id}`,
                                        ),
                                        typeName,
                                        column.type,
                                      );
                                    }}
                                    disabled={
                                      checkStatus === 2 ||
                                      checkStatus === 3 ||
                                      checkStatus === 4
                                    }
                                  >
                                    <RemoveIcon
                                      sx={{ color: "#FC5C57", fontSize: 16 }}
                                    />
                                  </Button>
                                ) : null}
                                <TextField
                                  {...field}
                                  type={column.type}
                                  placeholder={column.label}
                                  title={column.label}
                                  onChange={(e) => {
                                    let value: string | number = e.target.value;
                                    if (
                                      column.id === "quantity" ||
                                      column.id === "quantityAdjusted"
                                    )
                                      value = Math.max(
                                        0,
                                        Math.min(110000, Number(value)),
                                      );
                                    if (
                                      column.type === "number" &&
                                      (Number(value) < 0 || !Number(value))
                                    )
                                      value = "";
                                    handleInputChange(
                                      index,
                                      column.id,
                                      value,
                                      typeName,
                                      column.type,
                                    );
                                    field.onChange(value);
                                  }}
                                  onFocus={(e) => {
                                    if (e.target.value === "0") {
                                      e.target.value = "";
                                      field.onChange("");
                                    }
                                    params?.id
                                      ? (previousValueRef.current =
                                          e.target.value)
                                      : "";
                                  }}
                                  onBlur={(e) => {
                                    if (
                                      (column.id === "quantity" ||
                                        column.id === "quantityAdjusted") &&
                                      (e.target.value === "" ||
                                        Number(e.target.value) < 1)
                                    ) {
                                      e.target.value = "1";
                                      field.onChange("1");
                                      handleInputChange(
                                        index,
                                        column.id,
                                        1,
                                        typeName,
                                        column.type,
                                      );
                                    } else if (
                                      e.target.value === "" &&
                                      column.type === "number"
                                    ) {
                                      e.target.value = "0";
                                      field.onChange("0");
                                      handleInputChange(
                                        index,
                                        column.id,
                                        parseInt(e.target.value) || 0,
                                        typeName,
                                        column.type,
                                      );
                                    }
                                    if (params?.id && !row.id) {
                                      if (
                                        previousValueRef.current !==
                                        e.target.value
                                      ) {
                                        handleItem?.(row, typeName, "create");
                                      }
                                    } else if (params?.id && row.id) {
                                      if (
                                        previousValueRef.current !==
                                        e.target.value
                                      ) {
                                        handleItem?.(row, typeName, "update");
                                      }
                                    }
                                  }}
                                  defaultValue={
                                    column.id === "unitPrice" ||
                                    column.id === "discount" ||
                                    column.id === "unitCostAdjusted"
                                      ? 0
                                      : column.id === "quantity" ||
                                          column.id === "quantityAdjusted"
                                        ? 1
                                        : ""
                                  }
                                  disabled={
                                    column.id === "quantity" && enableQuantity
                                      ? false
                                      : column.id === "unitCostAdjusted" &&
                                          typeName === "StockAdjustmentItem" &&
                                          form.watch(
                                            `${typeName}.${index}.adjustmentType`,
                                          )?.value !== 2
                                        ? true
                                        : checkStatus === 2 ||
                                          checkStatus === 3 ||
                                          checkStatus === 4
                                  }
                                  sx={
                                    column.field === "quantity"
                                      ? {
                                          flex: 1,
                                          minWidth: 48,
                                          "& .MuiInputBase-root": {
                                            height: 32,
                                          },
                                          "& input": {
                                            textAlign: "center",
                                            padding: "4px 6px",
                                          },
                                          "& input[type=number]": {
                                            MozAppearance: "textfield",
                                          },
                                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                            {
                                              WebkitAppearance: "none",
                                              margin: 0,
                                            },
                                        }
                                      : { width: "100%" }
                                  }
                                />
                                {column.field === "quantity" && !params?.id ? (
                                  <Button
                                    aria-label="increase"
                                    sx={{
                                      ...quantityBtnSx,
                                      borderRadius: "0 6px 6px 0",
                                      background: "#17AE361A !important",
                                      borderColor: "#17AE3680",
                                    }}
                                    onClick={() => {
                                      form.setValue(
                                        `${typeName}.${index}.${column.id}`,
                                        Math.max(
                                          0,
                                          Math.min(
                                            110000,
                                            Number(
                                              form.watch(
                                                `${typeName}.${index}.${column.id}`,
                                              ) || 0,
                                            ) + 1,
                                          ),
                                        ),
                                      );
                                      handleInputChange(
                                        index,
                                        column.id,
                                        form.watch(
                                          `${typeName}.${index}.${column.id}`,
                                        ),
                                        typeName,
                                        column.type,
                                      );
                                    }}
                                    disabled={
                                      checkStatus === 2 ||
                                      checkStatus === 3 ||
                                      checkStatus === 4
                                    }
                                  >
                                    <AddIcon
                                      sx={{ color: "#17AE36", fontSize: 16 }}
                                    />
                                  </Button>
                                ) : null}
                              </Box>
                            )}
                          />
                        )}
                      </TableCell>
                    ))}
                  {(row.id || viewDelete) && !hideDelete ? (
                    <TableCell
                      onClick={() => {
                        if (checkStatus === 1 || !params?.id || enableDelete) {
                          DeleteItem(row, typeName, index);
                          // columns.map((column) =>
                          //   form.resetField(`${typeName}.${index}.${column.id}`)
                          // );
                        }
                      }}
                      className="text-end hide-on-print"
                      sx={{ width: "6%" }}
                    >
                      <DeleteIcon />
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={(rows || []).length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="hide-on-print"
        labelRowsPerPage={t("rowsPerPage")}
      />
    </Box>
  );
};
